import { Aggregation, AggregationOption, Maybe } from '@magentoTypes'
import { Accordion as AccordionReach } from '@reach/accordion'
import { CustomAccordionItem } from 'elements/Accordion/CustomAccordionItem'
import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'
import { parseFilterString } from '../lib/parseFilterString'
import { FilterCheckbox } from './FilterCheckbox'
import clsx from 'clsx'

type Props = {
  aggregations: Aggregation[]
  initialOpen?: number
  filter: string
  setFilter: (s: string) => void
}

export const Filter = ({ aggregations, initialOpen, filter, setFilter }: Props) => {
  const { t } = useTranslation()
  const [indices, setIndices] = useState<number[]>(initialOpen !== undefined ? [initialOpen] : [])
  const [isIndicesSetted, setIndicesSetted] = useState(false)

  const toggleItem = (toggledIndex: number) => {
    if (indices?.includes(toggledIndex)) {
      setIndices(indices?.filter((currentIndex) => currentIndex !== toggledIndex))
    } else {
      setIndices([...indices, toggledIndex].sort())
    }
  }

  useEffect(() => {
    if (initialOpen === undefined) {
      const activeFilters = parseFilterString(filter)
      setIndices((prev) =>
        Array.from(
          new Set(
            (
              [
                ...prev,
                ...Object.keys(activeFilters).map((filter) =>
                  aggregations.findIndex((aggregation) => aggregation.attribute_code === filter),
                ),
              ] as number[]
            ).filter((index) => typeof index === 'number'),
          ),
        ),
      )
    }

    setIndicesSetted(true)
  }, [aggregations, filter, initialOpen])

  const activeSubOptions = (
    options: Array<AggregationOption & { originalValue?: string }>,
  ): string => {
    let str = ''

    options.forEach(
      (option) =>
        filter?.includes(option.originalValue ?? option.value) &&
        (str += `${str !== '' ? ', ' : ''}${option.label}`),
    )

    return str
  }

  return isIndicesSetted ? (
    <AccordionReach
      index={indices as number[]}
      onChange={toggleItem}
      className="w-full border-b border-grey-border"
      collapsible
      multiple
    >
      {aggregations?.map((item: Aggregation, i) =>
        item ? (
          <CustomAccordionItem
            key={item.attribute_code}
            itemHeading={`${item.label}`}
            itemText={
              item.options
                ? activeSubOptions(item.options as AggregationOption[] & { originalValue: string })
                : undefined
            }
            className={clsx(
              !indices?.includes(i) && 'hover:text-grey-dark',
              indices?.includes(i) && 'mb-2',
            )}
            index={i}
            classNameButtonTextContainer="lg:pl-0"
          >
            <ul
              className={clsx(
                item.attribute_code === 'benuta_form_new' &&
                  'grid grid-cols-3 gap-2 sm:grid-cols-4',
              )}
            >
              {item.options?.map((option: Maybe<AggregationOption & { originalValue?: string }>) =>
                item?.attribute_code === 'benuta_floor_heating' ? (
                  <li key={option?.value} className="">
                    <FilterCheckbox
                      label={option?.label == '1' ? t('Yes') : t('No')}
                      value={option?.value || ''}
                      icon={option?.value as string}
                      attributeCode={item.attribute_code}
                      filter={filter}
                      setFilter={setFilter}
                    />
                  </li>
                ) : // dont display "weird" options like the 0€ Pricegroup option
                item?.attribute_code === 'benuta_pricegroup_new' && option?.value === '0' ? null : (
                  <li key={option?.value} className="">
                    <FilterCheckbox
                      label={option?.label || ''}
                      // values for constructor differs in value(magento sending ids, while constructor labels)(and colors and forms are basing on ids). So we need to pass originalValue when using constructor
                      value={option?.originalValue ? option?.originalValue : option?.value || ''}
                      icon={option?.value as string}
                      attributeCode={item.attribute_code}
                      filter={filter}
                      setFilter={setFilter}
                    />
                  </li>
                ),
              )}
            </ul>
          </CustomAccordionItem>
        ) : null,
      )}
    </AccordionReach>
  ) : (
    <></>
  )
}
